import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Star } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RatingInput = ({ reviewId, onRatingChange }) => {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [userRating, setUserRating] = useState(null);
  const [hoverRating, setHoverRating] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reviewStats, setReviewStats] = useState(null);

  useEffect(() => {
    fetchReviewStats();
    if (isAuthenticated) {
      fetchUserRating();
    }
  }, [isAuthenticated, reviewId]);

  const fetchReviewStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ratings/review/${reviewId}`);
      if (response.ok) {
        const data = await response.json();
        setReviewStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
    }
  };

  const fetchUserRating = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ratings/user/${reviewId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUserRating(data.rating);
      }
    } catch (error) {
      console.error('Failed to fetch user rating:', error);
    }
  };

  const handleRating = async (score) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ratings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ review_id: reviewId, score }),
      });

      if (response.ok) {
        setUserRating(score);
        fetchReviewStats(); // Refresh stats
        if (onRatingChange) onRatingChange(score);
      }
    } catch (error) {
      console.error('Failed to submit rating:', error);
    } finally {
      setLoading(false);
    }
  };

  const displayRating = hoverRating || userRating;

  return (
    <div className="p-4 bg-[#121917] border border-[#52ea88]/10">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-[#E6FFF6]">{t('reviews.yourRating')}</h4>
        {reviewStats && reviewStats.total_ratings > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-[#9FBFB4] cursor-help">
                  {reviewStats.average_score}/10 ({reviewStats.total_ratings} {t('reviews.ratings')})
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="bg-[#0B0F0E] border border-[#52ea88]/30 text-[#E6FFF6] text-xs p-2"
              >
                Average user rating
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* 1-10 Rating Scale */}
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
          <TooltipProvider key={score}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleRating(score)}
                  onMouseEnter={() => setHoverRating(score)}
                  onMouseLeave={() => setHoverRating(null)}
                  disabled={loading}
                  className={`relative w-7 h-7 flex items-center justify-center text-xs font-medium transition-all disabled:opacity-50 ${
                    displayRating && score <= displayRating
                      ? 'bg-[#52ea88] text-[#0B0F0E]'
                      : 'bg-[#0B0F0E] text-[#9FBFB4] hover:bg-[#52ea88]/20 hover:text-[#52ea88]'
                  } ${userRating === score ? 'ring-1 ring-[#52ea88]' : ''}`}
                >
                  {score}
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="bg-[#0B0F0E] border border-[#52ea88]/30 text-[#E6FFF6] text-xs p-1"
              >
                {getRatingLabel(score)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>

      {/* Current Rating Display */}
      <div className="mt-3 flex items-center justify-between">
        {userRating ? (
          <p className="text-xs text-[#52ea88]">
            {t('reviews.youRated')}: {userRating}/10 - {getRatingLabel(userRating)}
          </p>
        ) : (
          <p className="text-xs text-[#9FBFB4]/60">
            {isAuthenticated ? t('reviews.clickToRate') : t('reviews.signInToRate')}
          </p>
        )}
      </div>
    </div>
  );
};

// Helper function to get rating label
function getRatingLabel(score) {
  if (score <= 2) return 'Poor';
  if (score <= 4) return 'Below Average';
  if (score <= 5) return 'Average';
  if (score <= 6) return 'Above Average';
  if (score <= 7) return 'Good';
  if (score <= 8) return 'Great';
  if (score <= 9) return 'Excellent';
  return 'Masterpiece';
}

// Community Rating Display (for showing aggregate ratings)
export const CommunityRating = ({ reviewId }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ratings/review/${reviewId}`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch rating stats:', error);
      }
    };
    fetchStats();
  }, [reviewId]);

  if (!stats || stats.total_ratings === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <Star className="w-4 h-4 text-[#52ea88] fill-[#52ea88]" />
      <span className="text-[#E6FFF6] font-medium">{stats.average_score}</span>
      <span className="text-[#9FBFB4] text-sm">({stats.total_ratings})</span>
    </div>
  );
};

export default RatingInput;
